import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { useAuth } from "../AuthProvider";
import { authClient, authResponse } from "../client";
import { downloadAccountData, deleteAccount } from "../account-data";
import { errorCode } from "../errors";
import type {
  AccountSecurityManagement,
  AccountSecuritySnapshot,
  PasswordChangeInput,
} from "../types";

export function useAccountSecurity(): AccountSecurityManagement & {
  changePassword(input: PasswordChangeInput): Promise<void>;
} {
  const { user, refreshSession, recoverRejectedSession } = useAuth();
  const queryClient = useQueryClient();
  const [search] = useSearchParams();
  const key = ["auth", "security", user?.id] as const;
  const security = useQuery({
    queryKey: key,
    queryFn: async ({ signal }) =>
      authResponse<AccountSecuritySnapshot>(
        await authClient.accountSecurity.mutate({}, { signal }),
      ),
    enabled: user !== null,
    retry: false,
    staleTime: 30_000,
  });
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: key });
  };
  async function update(request: Promise<{ status: number; body?: unknown }>) {
    try {
      authResponse(await request);
    } catch (cause) {
      if (errorCode(cause) === "EMAIL_CHANGE_NOT_PENDING") await refresh();
      throw cause;
    }
    await refresh();
  }
  const outcome = search.get("emailChange");
  return {
    snapshot: security.data,
    state: !user
      ? "unavailable"
      : security.isPending
        ? "loading"
        : security.isError
          ? "unavailable"
          : "ready",
    emailChangeOutcome:
      outcome === "completed" ||
      outcome === "conflict" ||
      outcome === "expired" ||
      outcome === "invalid"
        ? outcome
        : undefined,
    refresh,
    requestFreshSignIn: recoverRejectedSession,
    beginEmailChange: (newEmail) =>
      update(authClient.beginEmailChange.mutate({ newEmail })),
    cancelEmailChange: () => update(authClient.cancelEmailChange.mutate({})),
    resendEmailChange: () => update(authClient.resendEmailChange.mutate({})),
    establishPassword: (newPassword) =>
      update(authClient.establishPassword.mutate({ newPassword })),
    revokeSession: (sessionId) =>
      update(authClient.revokeSession.mutate({ sessionId })),
    revokeOtherSessions: () =>
      update(authClient.revokeOtherSessions.mutate({})),
    changePassword: async (input) => {
      await update(
        authClient.changePassword.mutate({
          ...input,
          revokeOtherSessions: true,
        }),
      );
      await refreshSession();
    },
    deleteAccount: async () => {
      await deleteAccount();
      await recoverRejectedSession();
    },
    exportAccountData: downloadAccountData,
  };
}
